import { useCallback, useRef, useState } from "react";
import { logger } from "../services/telemetry";
import { apiStatus } from "../analytics/tracking";

export interface ActionState<A extends unknown[]> {
  /** Resolves true on success, false on failure (error lands in `error`). */
  run: (...args: A) => Promise<boolean>;
  busy: boolean;
  error: unknown;
  reset: () => void;
}

/* A minimal mutation wrapper for dialogs and row menus: busy flag while the
 * client call is in flight, last error kept for inline display, no throw out
 * of event handlers. Callers bump their query `version` when run resolves
 * true. Deliberately not a cache-invalidation framework. */
export function useAction<A extends unknown[]>(
  fn: (...args: A) => Promise<unknown>,
  /** Optional operation label (e.g. "createApiKey") attached to the failure
   *  log so mutations are sliceable in Loki. */
  origin?: string,
): ActionState<A> {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  // Keep the latest fn without making it part of run's identity.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(
    async (...args: A): Promise<boolean> => {
      setBusy(true);
      setError(undefined);
      try {
        await fnRef.current(...args);
        return true;
      } catch (err) {
        setError(err);
        // Surface the failure to OTEL. Mutation errors are shown inline in the UI
        // and would otherwise never reach Loki. Error-only — no latency plumbing.
        logger.error(
          err instanceof Error ? err : new Error(String(err)),
          "action failed",
          { ...(origin ? { origin } : {}), status: apiStatus(err) },
        );
        return false;
      } finally {
        setBusy(false);
      }
    },
    [origin],
  );

  const reset = useCallback(() => setError(undefined), []);

  return { run, busy, error, reset };
}
